/**
 * Step 1 — input recording (owner: foundation; stable). Derives presses, holds, double-taps and the press buffer
 * from the raw InputFrames, inside SimState so it is deterministic and rolls back with everything else.
 *
 * Semantics (ARCHITECTURE §5 Controls, state.ts invariant 7):
 *  - `cur`/`prev`: this tick's and last tick's frame. `held[b]`: consecutive frames held (0 = up).
 *  - A press (down now, up last tick) sets `lastPress[b] = tick` (the tick being stepped), shifts the old value to
 *    `prevPress[b]`, and arms `buffer[b] = MOVE.inputBufferFrames`.
 *  - Buffers count down by 1 per tick EXCEPT during hit-stop, so a press made during a freeze survives it.
 *  - A consumer that acts on a buffered press clears it with `consumePress` (one press = one action).
 * Recording runs every tick, hit-stop included.
 */
import { INPUT_MASK, MOVE, type InputFrame } from '@hf/shared';
import { BUTTON_COUNT, type BtnIndex } from '../enums';
import type { InputHistory, SimState } from '../state';

export const recordInputs = (s: SimState, inputs: readonly InputFrame[]): void => {
  const frozen = s.hitStop > 0;
  for (let i = 0; i < 2; i++) {
    const h = s.players[i as 0 | 1].input;
    const f = (inputs[i] ?? 0) & INPUT_MASK;
    h.prev = h.cur;
    h.cur = f;
    for (let b = 0; b < BUTTON_COUNT; b++) {
      const down = (f >> b) & 1;
      const was = (h.prev >> b) & 1;
      if (down && !was) {
        h.prevPress[b] = h.lastPress[b]!;
        h.lastPress[b] = s.tick;
        h.buffer[b] = MOVE.inputBufferFrames;
      } else if (!frozen && h.buffer[b]! > 0) {
        h.buffer[b] = h.buffer[b]! - 1;
      }
      h.held[b] = down ? h.held[b]! + 1 : 0;
    }
  }
};

/** Button is down this tick. */
export const isHeld = (h: InputHistory, b: BtnIndex): boolean => ((h.cur >> b) & 1) === 1;
/** Pressed on exactly this tick (not buffered). */
export const pressedNow = (h: InputHistory, b: BtnIndex, tick: number): boolean => h.lastPress[b] === tick;
/** A press is waiting in the buffer (pressed within the last MOVE.inputBufferFrames unfrozen ticks). */
export const hasBufferedPress = (h: InputHistory, b: BtnIndex): boolean => h.buffer[b]! > 0;
/** Consume a buffered press: returns true once per press. */
export const consumePress = (h: InputHistory, b: BtnIndex): boolean => {
  if (h.buffer[b]! <= 0) return false;
  h.buffer[b] = 0;
  return true;
};
/**
 * Keep a buffered press alive through the next tick (for a consumer still waiting for its window, e.g. a ▼ pressed
 * on take-off that must reach the fake-jump cancel window). No-op if nothing is buffered.
 */
export const keepPress = (h: InputHistory, b: BtnIndex): void => {
  if (h.buffer[b] === 1) h.buffer[b] = 2;
};
/** Double-tap: pressed this tick and the previous press was ≤ MOVE.doubleTapWindow ticks earlier. */
export const doubleTapped = (h: InputHistory, b: BtnIndex, tick: number): boolean =>
  h.lastPress[b] === tick && h.prevPress[b]! >= 0 && tick - h.prevPress[b]! <= MOVE.doubleTapWindow;
/**
 * Chord (e.g. SPIN = ▼ + Action): one of the two was pressed this tick and the other was pressed within
 * MOVE.chordWindowFrames ticks (and is still held).
 */
export const chordPressed = (h: InputHistory, a: BtnIndex, b: BtnIndex, tick: number): boolean => {
  const la = h.lastPress[a]!;
  const lb = h.lastPress[b]!;
  if (la < 0 || lb < 0 || (la !== tick && lb !== tick)) return false;
  const gap = la > lb ? la - lb : lb - la;
  return gap <= MOVE.chordWindowFrames && isHeld(h, a) && isHeld(h, b);
};
